import { useState } from "react";
import { ChevronDown, TrendingUp, Percent, DollarSign, X, Pencil, Check } from "lucide-react";
import { useGetPrenotazioni, useGetStrutture, useUpdatePrenotazione } from "../lib/supabaseQueries";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const PIATT_COLOR: Record<string,string> = {
  "Airbnb":     "bg-red-50 text-red-700",
  "Booking.com":"bg-blue-50 text-blue-700",
  "VRBO":       "bg-green-50 text-green-700",
  "Diretto":    "bg-primary/5 text-primary",
  "Altro":      "bg-muted/60 text-muted-foreground",
};
const fmt = (n: number) => "€" + n.toLocaleString("it-IT");

export default function FinanzeEntrate() {
  const { data: prenotazioni = [], isLoading } = useGetPrenotazioni();
  const { data: strutture = [] } = useGetStrutture();
  const updatePren = useUpdatePrenotazione();

  const [filMese,   setFilMese]   = useState("");
  const [filStrutt, setFilStrutt] = useState("");
  const [filPiatt,  setFilPiatt]  = useState("");
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    commissione: 0,
    cedolare: 0,
    tasse: 0,
  });

  const entrate = prenotazioni.filter(p => Number(p.importo) > 0);

  const filtered = entrate.filter(p => {
    const data = p.data_checkin || p.check_in || "";
    const mNum = data.slice(5, 7);
    if (filMese && mNum !== String(MESI.indexOf(filMese) + 1).padStart(2, "0")) return false;
    if (filStrutt && p.struttura_nome !== filStrutt) return false;
    if (filPiatt && p.piattaforma !== filPiatt) return false;
    return true;
  });

  const totLordo = filtered.reduce((a, p) => a + (Number(p.importo) || 0), 0);
  const totComm  = filtered.reduce((a, p) => a + (Number((p as any).commissione) || 0), 0);
  const totCedolare = filtered.reduce((a, p) => a + (Number((p as any).cedolare) || 0), 0);
  const totTasse = filtered.reduce((a, p) => a + (Number((p as any).tasse) || 0), 0);
  const totNetto = totLordo - totComm - totCedolare - totTasse;

  const piattaforme = [...new Set(entrate.map(p => p.piattaforma).filter(Boolean))];
  const struttureNomi = strutture.map(s => s.nome);

  const startEdit = (p: any) => {
    setEditRowId(p.id);
    setEditValues({
      commissione: Number((p as any).commissione) || 0,
      cedolare: Number((p as any).cedolare) || 0,
      tasse: Number((p as any).tasse) || 0,
    });
  };

  const saveEdit = (id: string) => {
    updatePren.mutate({ 
      id: id as any, 
      data: { 
        commissione: editValues.commissione,
        cedolare: editValues.cedolare,
        tasse: editValues.tasse,
      } as any 
    });
    setEditRowId(null);
  };

  if (isLoading) return <div className="py-20 text-center text-muted-foreground text-sm">Caricamento...</div>;

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-foreground">Entrate</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Ricavi dalle prenotazioni. Commissioni, Cedolare e Tasse sono modificabili per riga. Il netto si ricalcola automaticamente.
        </p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-4 w-4 text-green-600"/><span className="text-xs text-muted-foreground">Lordo</span></div>
          <div className="text-xl font-bold text-green-700">{fmt(totLordo)}</div>
          <div className="text-xs text-muted-foreground">{filtered.length} prenotazioni</div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-1"><Percent className="h-4 w-4 text-red-400"/><span className="text-xs text-muted-foreground">Totale Detrazioni</span></div>
          <div className="text-xl font-bold text-red-500">-{fmt(totComm + totCedolare + totTasse)}</div>
          <div className="text-xs text-muted-foreground">
            Comm: {fmt(totComm)} · Ced: {fmt(totCedolare)} · Tax: {fmt(totTasse)}
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="h-4 w-4 text-primary"/><span className="text-xs text-muted-foreground">Netto incassato</span></div>
          <div className="text-xl font-bold text-primary">{fmt(totNetto)}</div>
          <div className="text-xs text-muted-foreground">
            {totLordo > 0 ? ((totNetto / totLordo) * 100).toFixed(1) : 0}% del lordo
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { val: filMese,   set: setFilMese,   opts: MESI,          label: "Mese" },
          { val: filStrutt, set: setFilStrutt, opts: struttureNomi, label: "Struttura" },
          { val: filPiatt,  set: setFilPiatt,  opts: piattaforme as string[], label: "Piattaforma" },
        ].map(({ val, set, opts, label }) => (
          <div key={label} className="relative">
            <select value={val} onChange={e => set(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-border bg-card text-sm text-foreground focus:outline-none">
              <option value="">{label}</option>
              {opts.map(o => <option key={o}>{o}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          </div>
        ))}
        {(filMese || filStrutt || filPiatt) && (
          <button onClick={() => { setFilMese(""); setFilStrutt(""); setFilPiatt(""); }}
            className="px-3 py-2 rounded-xl border border-border bg-card text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <X className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      {/* Tabella */}
      {filtered.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center">
          <TrendingUp className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-foreground mb-1">Nessuna prenotazione trovata</p>
          <p className="text-sm text-muted-foreground">Le entrate vengono generate automaticamente dalle prenotazioni.</p>
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  {["Check-in","Ospite","Struttura","Piattaforma","Lordo","Commissione","Cedolare","Tasse","Netto","Azioni"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const lordo = Number(p.importo) || 0;
                  const comm  = Number((p as any).commissione) || 0;
                  const cedolare = Number((p as any).cedolare) || 0;
                  const tasse = Number((p as any).tasse) || 0;
                  const netto = lordo - comm - cedolare - tasse;
                  const isEditing = editRowId === p.id;
                  return (
                    <tr key={p.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{p.data_checkin || p.check_in || "—"}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{p.ospite_nome}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{p.struttura_nome || "—"}{p.camera_nome ? ` · ${p.camera_nome}` : ""}</td>
                      <td className="px-4 py-3">
                        {p.piattaforma
                          ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PIATT_COLOR[p.piattaforma] ?? "bg-muted/60 text-muted-foreground"}`}>{p.piattaforma}</span>
                          : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">{fmt(lordo)}</td>
                      {isEditing ? (
                        <>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">€</span>
                              <input 
                                type="number" 
                                value={editValues.commissione} 
                                onChange={e => setEditValues(v => ({ ...v, commissione: +e.target.value }))}
                                min={0} 
                                className="w-16 px-2 py-1 rounded-lg border border-primary text-sm focus:outline-none" 
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">€</span>
                              <input 
                                type="number" 
                                value={editValues.cedolare} 
                                onChange={e => setEditValues(v => ({ ...v, cedolare: +e.target.value }))}
                                min={0} 
                                className="w-16 px-2 py-1 rounded-lg border border-primary text-sm focus:outline-none" 
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">€</span>
                              <input 
                                type="number" 
                                value={editValues.tasse} 
                                onChange={e => setEditValues(v => ({ ...v, tasse: +e.target.value }))}
                                min={0} 
                                className="w-16 px-2 py-1 rounded-lg border border-primary text-sm focus:outline-none" 
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 font-bold text-green-700">{fmt(lordo - editValues.commissione - editValues.cedolare - editValues.tasse)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => saveEdit(p.id)} className="p-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20"><Check className="h-3.5 w-3.5" /></button>
                              <button onClick={() => setEditRowId(null)} className="p-1 rounded-lg hover:bg-muted/50 text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3">
                            <span className="text-red-500 text-xs font-medium">{comm > 0 ? `-${fmt(comm)}` : <span className="text-muted-foreground">—</span>}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-orange-500 text-xs font-medium">{cedolare > 0 ? `-${fmt(cedolare)}` : <span className="text-muted-foreground">—</span>}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-blue-500 text-xs font-medium">{tasse > 0 ? `-${fmt(tasse)}` : <span className="text-muted-foreground">—</span>}</span>
                          </td>
                          <td className="px-4 py-3 font-bold text-green-700">{fmt(netto)}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => startEdit(p)}
                              className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/40 border-t border-border">
                  <td colSpan={4} className="px-4 py-3 text-xs font-bold text-muted-foreground">TOTALE ({filtered.length} prenotazioni)</td>
                  <td className="px-4 py-3 text-sm font-bold text-foreground">{fmt(totLordo)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-red-500">-{fmt(totComm)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-orange-500">-{fmt(totCedolare)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-500">-{fmt(totTasse)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-green-700">{fmt(totNetto)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
